import { Item } from '@factorialco/deprecated-design-system'
import {
  A3innuvaAbsenceFragment,
  A3innuvaClassificationTypeFragment,
  A3innuvaCompanyCodeFragment,
  A3innuvaCompensationCodeFragment,
  A3innuvaContractTypesFragment,
  A3innuvaContractsAgreementCodeFragment,
  A3innuvaContractsCnoOccupationFragment,
  A3innuvaContractsJobPositionFragment,
  A3innuvaContractsOccupationCodeFragment,
  A3innuvaContributionTypesFragment,
  A3innuvaCostCenterCodesFragment,
  A3innuvaEducationLevelFragment,
  A3innuvaOrganizationalUnitFragment,
  A3innuvaRegimeCodeFragment,
  A3innuvaTariffGroupCodeFragment,
  A3innuvaWorkplaceFragment,
} from 'generated/resources/graphql'
import { kebabCase } from 'lodash'

import i18n from 'modules/core/lib/i18n'

interface Codeable {
  code: string
  name: string
}

const emptyOption = (): Item[] => [{ value: '', label: `-- ${i18n.t('common.none')} --` }]

export const absencesToOptions = (absences: A3innuvaAbsenceFragment[]): Item[] => {
  if (absences.length == 0) {
    return []
  }

  const typeOptions = absences.map((absence) => {
    return {
      value: absence.identifier,
      label: absence.name,
    }
  })

  return withDefaultOption(typeOptions.sort((a, b) => a.label.localeCompare(b.label)))
}

export const organizationalUnitsToOptions = (
  types: A3innuvaOrganizationalUnitFragment[]
): Item[] => {
  if (types.length == 0) {
    return []
  }

  const typeOptions = types.map((type) => {
    return {
      value: type.id,
      label: type.displayName,
    }
  })

  return withDefaultOption(typeOptions)
}

export const companyCodesToOptions = (types: A3innuvaCompanyCodeFragment[]): Item[] => {
  return codeableToOptions(types)
}

export const educationLevelsToOptions = (types: A3innuvaEducationLevelFragment[]): Item[] => {
  if (types.length == 0) return []

  const typeOptions = types.map((type) => ({
    value: type.code,
    label: fullName(type.code, type.name),
  }))

  return withDefaultOption(typeOptions)
}

const codeableFullName = (code: string, name: string) => `${code} - ${name}`

export const codeableFullCode = (code: string, name: string) =>
  kebabCase(codeableFullName(code, name))

const codeableFullNameOrNone = (code: string, name: string) =>
  code ? codeableFullName(code, name) : `-- ${i18n.t('common.none')} --`

export const fullName = (code: string, name: string) => codeableFullNameOrNone(code, name)

export const cnoOccupationsToOptions = (
  types: A3innuvaContractsCnoOccupationFragment[]
): Item[] => {
  if (types.length == 0) return []

  const typeOptions = types.map((type) => ({
    value: type.code,
    label: fullName(type.code, type.name),
  }))

  return withDefaultOption(typeOptions)
}

export const occupationCodesToOptions = (
  types: A3innuvaContractsOccupationCodeFragment[]
): Item[] => {
  if (types.length == 0) return []

  const typeOptions = types.map((type) => ({
    value: type.code,
    label: fullName(type.code, type.name),
  }))

  return withDefaultOption(typeOptions)
}

export const jobPositionsToOptions = (types: A3innuvaContractsJobPositionFragment[]): Item[] => {
  if (types.length == 0) return []

  const typeOptions = types.map((type) => ({
    value: type.code,
    label: fullName(type.code, type.name),
  }))

  return withDefaultOption(typeOptions)
}

export const agreementCodesToOptions = (
  types: A3innuvaContractsAgreementCodeFragment[]
): Item[] => {
  if (types.length == 0) return []

  const typeOptions = types.map((type) => ({
    value: codeableFullCode(type.code, type.name),
    label: fullName(type.code, type.name),
  }))

  return withDefaultOption(typeOptions)
}

export const regimeCodesToOptions = (types: A3innuvaRegimeCodeFragment[]): Item[] => {
  return codeableToOptions(types)
}

export const classificationTypesToOptions = (
  types: A3innuvaClassificationTypeFragment[]
): Item[] => {
  if (types.length == 0) return withDefaultOption([])

  const typeOptions = types.map((type) => ({
    value: type.id,
    label: fullName(type.code, type.name),
  }))

  return withDefaultOption(typeOptions)
}

export const contractTypesToOptions = (types: A3innuvaContractTypesFragment[]): Item[] => {
  if (types.length == 0) return []

  const typeOptions = types.map((type) => ({
    value: type.code,
    label: fullName(type.code, type.name),
  }))

  return withDefaultOption(typeOptions)
}

export const contributionTypesToOptions = (types: A3innuvaContributionTypesFragment[]): Item[] => {
  if (types.length == 0) return []

  const typeOptions = types.map((type) => ({
    value: type.code,
    label: codeableFullName(type.code.length === 1 ? `0${type.code}` : type.code, type.name),
  }))

  return withDefaultOption(typeOptions)
}

export const cotizationGroupsToOptions = (types: A3innuvaTariffGroupCodeFragment[]): Item[] => {
  const mappedTypes = types.map((type) => ({
    code: type.code.toString(),
    name: type.name,
  }))
  return codeableToOptions(mappedTypes)
}

export const factorialCostCentersToOptions = (types: A3innuvaCostCenterCodesFragment[]): Item[] => {
  return codeableToOptions(types.map((type) => ({ code: type.code.toString(), name: type.name })))
}

export const workplacesToOptions = (types: A3innuvaWorkplaceFragment[]): Item[] => {
  if (types.length == 0) return []

  const typeOptions = types.map((type) => ({
    value: type.workplaceId,
    label: `${type.code} - ${type.name}`,
  }))

  return withDefaultOption(typeOptions)
}

export const getWorkplaceCode = (label: string) => {
  return label.split('-')[0]?.trim() ?? ''
}

const codeableToOptions = (types: Codeable[]): Item[] => {
  if (types.length == 0) {
    return []
  }

  const typeOptions = types.map((type) => {
    return {
      value: type.code,
      label: type.name,
    }
  })

  return withDefaultOption(typeOptions)
}

const withDefaultOption = (typeOptions: Item[]): Item[] => {
  return emptyOption().concat(...typeOptions)
}

export const compensationsToOptions = (types: A3innuvaCompensationCodeFragment[]): Item[] => {
  const mappedTypes = types.map((compensation) => ({
    label: `${compensation.code} - ${compensation.description}`,
    value: compensation.code,
  }))

  return withDefaultOption(mappedTypes)
}
